import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ImageOff, PackageSearch, ScanBarcode, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { useProducts } from '../../context/ProductsContext';
import { getByBarcode, getProductInsight, listProducts } from '../../api/inventory';
import { formatStock, isSoldByWeight } from '../../utils/soldBy';
import { tombstoneTarget } from '../../utils/productMerge';
import {
  asNumber,
  coverDisplay,
  formatInsightAt,
  formatInsightDay,
  formatInsightMoney,
  formatInsightQty,
  formatSignedQty,
  inclusiveDayCount,
  insightErrorCopy,
  reasonLabel,
} from './insightFormat';
import styles from './ProductInsight.module.css';

const PERIOD_OPTIONS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: 'month', label: 'Month' },
  { id: 'custom', label: 'Custom' },
];

const CHANNEL_OPTIONS = [
  { id: 'ALL', label: 'All' },
  { id: 'POS', label: 'POS' },
  { id: 'DELIVERY', label: 'Delivery' },
];

const HISTORY_LIMIT = 20;

function looksLikeBarcode(value) {
  return /^\d{6,18}$/.test(String(value || '').trim());
}

function statusLabel(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'ACTIVE') return 'Active';
  if (s === 'INACTIVE') return 'Inactive';
  if (!s) return '—';
  return s.charAt(0) + s.slice(1).toLowerCase();
}

function soldByText(item) {
  if (String(item?.sold_by || '').toUpperCase() === 'WEIGHT') {
    return `Sold by ${item.unit || 'kg'}`;
  }
  return 'Sold by each';
}

/** Round up to a clean axis max (1, 2, 5 × 10^n). */
function niceMax(v) {
  if (v <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(v));
  const n = v / pow;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return nice * pow;
}

function chartPeriodLabel(period, range) {
  if (period === 'today') return 'today';
  if (period === '7d') return 'last 7 days';
  if (period === '30d') return 'last 30 days';
  if (period === 'month') return 'this month';
  if (range?.from && range?.to) return `${formatInsightDay(range.from)} – ${formatInsightDay(range.to)}`;
  return '';
}

export default function ProductInsight() {
  const { user } = useAuth();
  const { categories, mergeProducts } = useProducts();
  const [searchParams, setSearchParams] = useSearchParams();
  const allowed = user?.groups?.includes('Admin') || user?.groups?.includes('Ops');

  const searchRef = useRef(null);
  const fetchSeq = useRef(0);
  const entryRef = useRef(false);

  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notInCatalog, setNotInCatalog] = useState(false);
  const [matches, setMatches] = useState([]);
  const [itemId, setItemId] = useState(() => searchParams.get('productId') || '');
  const [period, setPeriod] = useState('30d');
  const [channel, setChannel] = useState('ALL');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [insight, setInsight] = useState(null);
  const [insightQuery, setInsightQuery] = useState(null);
  const [history, setHistory] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rangeError, setRangeError] = useState('');
  const [activeBar, setActiveBar] = useState(null);

  const categoryById = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c.id, c));
    return map;
  }, [categories]);

  const categoryLabel = useCallback((id) => {
    if (!id) return null;
    return categoryById.get(id)?.name || id;
  }, [categoryById]);

  const focusSearch = useCallback(() => {
    searchRef.current?.focus();
  }, []);

  const leaveCard = useCallback(() => {
    fetchSeq.current += 1;
    setItemId('');
    setInsight(null);
    setInsightQuery(null);
    setHistory([]);
    setNextCursor(null);
    setInsightLoading(false);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const openId = useCallback((id) => {
    setError('');
    setNotInCatalog(false);
    setRangeError('');
    setMatches([]);
    setPeriod('30d');
    setChannel('ALL');
    setCustomFrom('');
    setCustomTo('');
    setInsight(null);
    setInsightQuery(null);
    setHistory([]);
    setNextCursor(null);
    setActiveBar(null);
    setItemId(id);
    setSearchParams({ productId: id }, { replace: true });
  }, [setSearchParams]);

  const applyInsight = useCallback((data, request) => {
    setInsight(data);
    setInsightQuery(request);
    setHistory(Array.isArray(data?.history?.movements) ? data.history.movements : []);
    setNextCursor(data?.history?.next_cursor || null);
  }, []);

  const handleInsightError = useCallback((err) => {
    const into = tombstoneTarget(err);
    if (into && into !== itemId) {
      openId(into);
      return;
    }
    const copy = insightErrorCopy(err);
    if (copy.kind === 'removed') {
      leaveCard();
      setError(copy.message);
      return;
    }
    if (copy.kind === 'forbidden') {
      leaveCard();
      setError(copy.message);
      return;
    }
    if (copy.kind === 'range' || copy.kind === 'query') {
      setRangeError(copy.message);
      return;
    }
    setError(copy.message);
  }, [itemId, leaveCard, openId]);

  useEffect(() => {
    focusSearch();
  }, [focusSearch]);

  useEffect(() => {
    const id = searchParams.get('productId');
    if (id && id !== itemId) setItemId(id);
  }, [searchParams, itemId]);

  const customReady = period !== 'custom' || (Boolean(customFrom) && Boolean(customTo));
  const customDays = period === 'custom' ? inclusiveDayCount(customFrom, customTo) : null;
  const customTooLong = period === 'custom' && customDays != null && customDays > 90;
  const customInvalid = period === 'custom' && customFrom && customTo && customDays == null;

  useEffect(() => {
    if (!allowed || !itemId || !user?.idToken || !customReady) return undefined;
    if (customTooLong) {
      setRangeError('Pick 90 days or fewer.');
      return undefined;
    }
    if (customInvalid) {
      setRangeError('That date range or filter is not valid.');
      return undefined;
    }

    const seq = fetchSeq.current + 1;
    fetchSeq.current = seq;
    let cancelled = false;
    const request = {
      itemId,
      period,
      channel,
      from: period === 'custom' ? customFrom : '',
      to: period === 'custom' ? customTo : '',
    };
    setInsightLoading(true);
    setRangeError('');
    setError('');

    getProductInsight(itemId, user.idToken, {
      period,
      channel,
      from: customFrom,
      to: customTo,
      limit: HISTORY_LIMIT,
    })
      .then((data) => {
        if (cancelled || seq !== fetchSeq.current) return;
        applyInsight(data, request);
      })
      .catch((err) => {
        if (cancelled || seq !== fetchSeq.current) return;
        handleInsightError(err);
      })
      .finally(() => {
        if (!cancelled && seq === fetchSeq.current) setInsightLoading(false);
      });

    return () => { cancelled = true; };
  }, [
    allowed,
    itemId,
    user?.idToken,
    period,
    channel,
    customFrom,
    customTo,
    customReady,
    customTooLong,
    customInvalid,
    applyInsight,
    handleInsightError,
  ]);

  const runNameSearch = useCallback(async (q) => {
    if (!user?.idToken) return;
    setBusy(true);
    setError('');
    setNotInCatalog(false);
    try {
      const { items } = await listProducts(user.idToken, {
        q,
        limit: 20,
        includeInactive: true,
      });
      mergeProducts(items || []);
      setMatches(items || []);
    } catch {
      setError('Could not search the catalog.');
      setMatches([]);
    } finally {
      setBusy(false);
    }
  }, [mergeProducts, user?.idToken]);

  const runBarcode = useCallback(async (code) => {
    if (!user?.idToken) return;
    setBusy(true);
    setError('');
    setNotInCatalog(false);
    try {
      const found = await getByBarcode(code, user.idToken);
      if (!found?.id) {
        leaveCard();
        setNotInCatalog(true);
        setMatches([]);
        return;
      }
      mergeProducts([found]);
      setQuery(found.barcode || code);
      openId(found.id);
    } catch (err) {
      const copy = insightErrorCopy(err);
      if (copy.kind === 'catalog' || err.status === 404) {
        leaveCard();
        setNotInCatalog(true);
        setMatches([]);
        return;
      }
      setError(copy.message);
    } finally {
      setBusy(false);
    }
  }, [leaveCard, mergeProducts, openId, user?.idToken]);

  useEffect(() => {
    if (entryRef.current || !user?.idToken) return undefined;
    const barcode = searchParams.get('barcode');
    const q = searchParams.get('q');
    if (!barcode && !q) return undefined;
    entryRef.current = true;
    const value = barcode || q;
    setQuery(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('barcode');
      next.delete('q');
      return next;
    }, { replace: true });
    if (looksLikeBarcode(value)) runBarcode(value);
    else {
      leaveCard();
      runNameSearch(value);
    }
    return undefined;
  }, [leaveCard, runBarcode, runNameSearch, searchParams, setSearchParams, user?.idToken]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q || !allowed) return;
    if (looksLikeBarcode(q)) {
      runBarcode(q);
      return;
    }
    leaveCard();
    runNameSearch(q);
  };

  const loadMore = async () => {
    if (!nextCursor || !itemId || !user?.idToken || historyLoading || customTooLong) return;
    setHistoryLoading(true);
    try {
      const data = await getProductInsight(itemId, user.idToken, {
        period,
        channel,
        from: customFrom,
        to: customTo,
        limit: HISTORY_LIMIT,
        cursor: nextCursor,
      });
      const page = Array.isArray(data?.history?.movements) ? data.history.movements : [];
      setHistory((prev) => [...prev, ...page]);
      setNextCursor(data?.history?.next_cursor || null);
    } catch (err) {
      const copy = insightErrorCopy(err);
      if (copy.kind === 'cursor') {
        setNextCursor(null);
        const seq = fetchSeq.current + 1;
        fetchSeq.current = seq;
        try {
          const data = await getProductInsight(itemId, user.idToken, {
            period,
            channel,
            from: customFrom,
            to: customTo,
            limit: HISTORY_LIMIT,
          });
          if (seq === fetchSeq.current) {
            applyInsight(data, {
              itemId,
              period,
              channel,
              from: period === 'custom' ? customFrom : '',
              to: period === 'custom' ? customTo : '',
            });
          }
        } catch (resetErr) {
          handleInsightError(resetErr);
        }
        return;
      }
      setError(copy.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const item = insight?.item || null;

  useEffect(() => {
    const into = item?.merged_into || item?.mergedInto;
    if (String(item?.status || '').toUpperCase() === 'MERGED' && into && into !== itemId) {
      openId(into);
    }
  }, [item, itemId, openId]);
  const sales = insight?.sales || null;
  const stock = insight?.stock || null;
  const cover = insight?.cover || null;
  const insightMatches = Boolean(
    insight
    && insightQuery
    && insightQuery.itemId === itemId
    && insightQuery.period === period
    && insightQuery.channel === channel
    && insightQuery.from === (period === 'custom' ? customFrom : '')
    && insightQuery.to === (period === 'custom' ? customTo : ''),
  );
  const showProfit = Boolean(
    insightMatches && sales && Object.prototype.hasOwnProperty.call(sales, 'gross_profit'),
  );
  const showLanding = !itemId;

  const yAxisMax = useMemo(() => {
    const series = insight?.series;
    if (!Array.isArray(series) || !series.length) return 1;
    return niceMax(Math.max(0, ...series.map((d) => asNumber(d.qty) || 0)));
  }, [insight]);

  const channelMax = useMemo(() => {
    const pos = asNumber(insight?.channels?.pos?.qty) || 0;
    const delivery = asNumber(insight?.channels?.delivery?.qty) || 0;
    return Math.max(1, pos, delivery);
  }, [insight]);

  const soldBy = item?.sold_by;
  const coverText = coverDisplay(cover?.days_of_cover);
  const coverIsEmpty = cover?.days_of_cover == null;
  const onHandQty = formatInsightQty(item?.stock_qty, soldBy);
  const avgDaily = asNumber(cover?.avg_daily_qty);

  if (!allowed) {
    return (
      <div className={styles.page}>
        <PageHeader eyebrow="Store · Ops" title="Product insight" />
        <p className={styles.errorBanner} role="alert">You do not have access to Product insight.</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Store · Ops"
        title="Product insight"
        subtitle={showLanding
          ? 'Scan a barcode, or search name / SKU.'
          : 'One product. Sales and stock for the selected period.'}
      />

      <form className={styles.searchBar} onSubmit={handleSubmit}>
        <div className={`${styles.searchField} ${busy || insightLoading ? styles.searchFieldBusy : ''}`}>
          <ScanBarcode size={18} className={styles.searchIcon} aria-hidden />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setNotInCatalog(false);
            }}
            placeholder="Scan barcode, or search name / SKU"
            autoComplete="off"
            inputMode="search"
            aria-label="Find product by barcode, name, or SKU"
          />
          {query && (
            <button
              type="button"
              className={styles.clearBtn}
              onClick={() => {
                setQuery('');
                setMatches([]);
                setNotInCatalog(false);
                setError('');
                leaveCard();
                focusSearch();
              }}
              aria-label="Clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </form>

      {showLanding && (
        <p className={styles.helper}>
          Scan writes the barcode here and opens the product. Search matches name or SKU.
          Unknown barcode stays on this screen.
        </p>
      )}

      {error && <p className={styles.errorBanner} role="alert">{error}</p>}
      {notInCatalog && <p className={styles.warnBanner} role="status">Not in catalog.</p>}

      {showLanding && (
        <section className={styles.card}>
          <header className={styles.cardHead}>
            <h2>Matches</h2>
            {matches.length > 0 && (
              <span className={styles.cardMeta}>{matches.length} result{matches.length === 1 ? '' : 's'}</span>
            )}
          </header>

          {!query.trim() && matches.length === 0 && (
            <div className={styles.emptyMatches}>
              <PackageSearch size={28} strokeWidth={1.5} />
              <p>Type a name or SKU, or scan a barcode, then press Enter.</p>
            </div>
          )}

          {query.trim() && matches.length === 0 && !busy && !notInCatalog && (
            <div className={styles.emptyMatches}>
              <p>No matches for &ldquo;{query.trim()}&rdquo;.</p>
            </div>
          )}

          {matches.length > 0 && (
            <ul className={styles.matchList}>
              {matches.map((p) => {
                const low = Number(p.lowStockThreshold) > 0
                  && Number(p.stock) <= Number(p.lowStockThreshold);
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      className={styles.matchRow}
                      onClick={() => {
                        setQuery(p.barcode || p.sku || p.name || '');
                        openId(p.id);
                      }}
                    >
                      <div className={styles.thumb}>
                        {p.images?.[0]?.url
                          ? <img src={p.images[0].url} alt="" />
                          : <ImageOff size={18} strokeWidth={1.5} />}
                      </div>
                      <div className={styles.matchBody}>
                        <span className={styles.matchName}>{p.name}</span>
                        <span className={styles.matchMeta}>
                          {[p.sku || '—', p.barcode || '—', categoryLabel(p.category), isSoldByWeight(p) ? (p.unit || 'kg') : 'Each']
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </div>
                      <div className={styles.matchStock}>
                        <span className={low ? styles.stockLow : undefined}>
                          {formatStock(p.stock, p)}
                          {!isSoldByWeight(p) ? ' pcs' : ''}
                        </span>
                        {Number(p.lowStockThreshold) > 0 && (
                          <span className={styles.reorderHint}>Reorder {p.lowStockThreshold}</span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {itemId && !item && insightLoading && (
        <p className={styles.helper} role="status">Loading product…</p>
      )}

      {item && sales && stock && cover && (
        <div
          className={`${styles.detailStack} ${insightLoading ? styles.cardBusy : ''}`}
          aria-busy={insightLoading}
        >
          <section className={`${styles.card} ${styles.identityCard}`}>
            <div className={styles.identityMain}>
              <div className={styles.identityThumb}>
                {item.image_urls?.[0]
                  ? <img src={item.image_urls[0]} alt="" />
                  : <ImageOff size={28} strokeWidth={1.5} />}
              </div>
              <div className={styles.identityBody}>
                <h2 className={styles.productName}>{item.name}</h2>
                <p className={styles.productMeta}>
                  {[
                    item.sku || '—',
                    item.barcode || '—',
                    categoryLabel(item.category),
                    soldByText(item),
                    item.gst_rate != null ? `GST ${item.gst_rate}%` : null,
                  ].filter(Boolean).join(' · ')}
                </p>
                <div className={styles.badgeRow}>
                  <span className={`${styles.badge} ${String(item.status).toUpperCase() === 'ACTIVE' ? styles.badgeActive : styles.badgeMuted}`}>
                    {statusLabel(item.status)}
                  </span>
                  {item.selling_price != null && (
                    <span className={`${styles.badge} ${styles.badgeNeutral}`}>
                      Sell {formatInsightMoney(item.selling_price)}
                    </span>
                  )}
                  {item.mrp != null && (
                    <span className={`${styles.badge} ${styles.badgeNeutral}`}>
                      MRP {formatInsightMoney(item.mrp)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.onHandBlock}>
              <span className={styles.onHandValue}>{onHandQty}</span>
              <span className={styles.onHandSub}>
                On hand
                {asNumber(item.reorder_level) != null ? ` · Reorder at ${formatInsightQty(item.reorder_level, soldBy)}` : ''}
              </span>
            </div>
          </section>

          <div className={styles.filterRow}>
            <div className={styles.segGroup} role="group" aria-label="Period">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`${styles.seg} ${period === opt.id ? styles.segActive : ''}`}
                  onClick={() => setPeriod(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className={styles.segGroup} role="group" aria-label="Channel">
              {CHANNEL_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`${styles.seg} ${channel === opt.id ? styles.segActive : ''}`}
                  onClick={() => setChannel(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {period === 'custom' && (
            <div className={styles.customRange}>
              <label className={styles.customField}>
                <span>From</span>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </label>
              <label className={styles.customField}>
                <span>To</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </label>
            </div>
          )}

          <p className={styles.channelNote}>
            Channel changes sales only. Stock totals stay for the whole period.
          </p>
          {rangeError && <p className={styles.warnBanner} role="alert">{rangeError}</p>}

          {!insightMatches && (
            <p className={styles.helper} role="status">
              {period === 'custom' && !customReady
                ? 'Choose a start and end date.'
                : insightLoading
                  ? 'Loading…'
                  : null}
            </p>
          )}

          {insightMatches && (
          <>
          <div className={styles.kpiGrid}>
            <KpiTile
              label="Qty sold"
              value={formatInsightQty(sales.qty_sold, soldBy)}
              hint="Recognized sales"
            />
            <KpiTile
              label="Revenue"
              value={formatInsightMoney(sales.revenue)}
              hint="GST inclusive"
            />
            {showProfit && (
              <KpiTile
                label="Gross profit"
                value={formatInsightMoney(sales.gross_profit)}
                hint={
                  asNumber(sales.margin_pct) != null
                    ? `Admin · ${sales.margin_pct}% of this product’s revenue`
                    : 'Admin'
                }
              />
            )}
            <KpiTile
              label="Days of cover"
              value={coverText}
              hint={
                coverIsEmpty || avgDaily == null
                  ? undefined
                  : `${onHandQty} on hand ÷ ${formatInsightQty(avgDaily, soldBy)} / day`
              }
              warn={cover.below_reorder === true}
              sentence={coverIsEmpty}
            />
            <KpiTile
              label="Receipts"
              value={formatInsightQty(stock.receipts, soldBy)}
              hint="Purchased"
              muted
            />
            <KpiTile
              label="Other outs"
              value={formatInsightQty(stock.other_outs, soldBy)}
              hint="Damage, expiry, not sales"
              muted
            />
            <KpiTile
              label="Returns restocked"
              value={formatInsightQty(stock.returns_restocked, soldBy)}
              muted
            />
            <KpiTile
              label="Net adjust"
              value={formatSignedQty(stock.net_adjust, soldBy)}
              muted
              valueClass={
                (asNumber(stock.net_adjust) || 0) < 0
                  ? styles.neg
                  : (asNumber(stock.net_adjust) || 0) > 0
                    ? styles.pos
                    : undefined
              }
            />
          </div>

          <div className={styles.chartsRow}>
            <section className={styles.card}>
              <header className={styles.cardHead}>
                <h2>
                  Qty sold by day
                  {chartPeriodLabel(period, insight.range) ? ` · ${chartPeriodLabel(period, insight.range)}` : ''}
                </h2>
              </header>
              <div className={styles.barChart} role="img" aria-label="Qty sold by day">
                <div className={styles.plot}>
                  <div className={styles.bars}>
                    {(insight.series || []).map((d, idx) => {
                      const qty = asNumber(d.qty) || 0;
                      const height = yAxisMax > 0
                        ? Math.max(qty > 0 ? 3 : 0, (qty / yAxisMax) * 100)
                        : 0;
                      const active = activeBar === idx;
                      const series = insight.series || [];
                      const showLabel = series.length <= 8
                        || idx === 0
                        || idx === series.length - 1
                        || idx === Math.floor(series.length / 2);
                      return (
                        <button
                          key={d.date || idx}
                          type="button"
                          className={styles.barCol}
                          title={`${formatInsightDay(d.date)}: ${formatInsightQty(qty, soldBy)}`}
                          onClick={() => setActiveBar(active ? null : idx)}
                        >
                          <div className={styles.barTrack}>
                            <div
                              className={`${styles.bar} ${active ? styles.barActive : ''}`}
                              style={{ height: `${height}%` }}
                            />
                          </div>
                          <span className={`${styles.barLabel} ${showLabel ? '' : styles.barLabelHidden}`}>
                            {formatInsightDay(d.date)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              {insight.range?.from && insight.range?.to && (
                <p className={styles.footnote}>
                  {formatInsightDay(insight.range.from)} – {formatInsightDay(insight.range.to)}
                </p>
              )}
            </section>

            <section className={styles.card}>
              <header className={styles.cardHead}>
                <h2>Where it sold</h2>
              </header>
              <ul className={styles.channelList}>
                <ChannelRow
                  label="POS"
                  qty={insight.channels?.pos?.qty}
                  max={channelMax}
                  soldBy={soldBy}
                  color="var(--color-secondary)"
                />
                <ChannelRow
                  label="Delivery"
                  qty={insight.channels?.delivery?.qty}
                  max={channelMax}
                  soldBy={soldBy}
                  color="#0D9488"
                />
              </ul>
              <p className={styles.channelTotal}>
                {formatInsightQty(sales.qty_sold, soldBy)} total
              </p>
              <div className={styles.subDrops}>
                <div className={styles.subDropsRow}>
                  <span>Subscription drops</span>
                  <strong>{formatInsightQty(stock.subscription_drops, soldBy)}</strong>
                </div>
                <p>Stock that left on a subscription run. Not included in qty sold or revenue.</p>
              </div>
            </section>
          </div>

          <div className={styles.detailRow}>
            <section className={styles.card}>
              <header className={styles.cardHead}>
                <h2>Open lots</h2>
              </header>
              {!insight.lots?.length ? (
                <p className={styles.emptyInline}>No open lots</p>
              ) : (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Lot</th>
                        <th>MRP</th>
                        <th>Sell</th>
                        <th>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {insight.lots.map((lot) => (
                        <tr key={lot.lot_id}>
                          <td className={styles.mono}>{lot.lot_id}</td>
                          <td>{formatInsightMoney(lot.mrp)}</td>
                          <td>{formatInsightMoney(lot.selling_price)}</td>
                          <td>{formatInsightQty(lot.qty, soldBy)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className={styles.card}>
              <header className={styles.cardHead}>
                <h2>Last receipt and last sale</h2>
              </header>
              <div className={styles.lastEvents}>
                <EventBlock
                  label="Last receipt"
                  event={insight.last_receipt}
                  soldBy={soldBy}
                  positive
                />
                <EventBlock
                  label="Last sale"
                  event={insight.last_sale}
                  soldBy={soldBy}
                />
              </div>
            </section>
          </div>

          <section className={styles.card}>
            <header className={styles.cardHead}>
              <h2>Stock history</h2>
              <span className={styles.cardMeta}>Newest first</span>
            </header>
            {history.length === 0 ? (
              <p className={styles.emptyInline}>No stock movements in this period.</p>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Reason</th>
                      <th>Change</th>
                      <th>Stock</th>
                      <th>Lot</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((row, i) => {
                      const delta = asNumber(row.qty_delta);
                      return (
                        <tr key={`${row.at || 'row'}-${row.reference || ''}-${i}`}>
                          <td>{formatInsightAt(row.at)}</td>
                          <td>{reasonLabel(row.reason_code)}</td>
                          <td className={delta != null && delta < 0 ? styles.neg : styles.pos}>
                            {formatSignedQty(row.qty_delta, soldBy)}
                          </td>
                          <td className={styles.mono}>
                            {formatInsightQty(row.qty_before, soldBy)} → {formatInsightQty(row.qty_after, soldBy)}
                          </td>
                          <td className={styles.mono}>{row.lot_id || '—'}</td>
                          <td>{row.created_by || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {nextCursor && (
              <button
                type="button"
                className={styles.loadMore}
                onClick={loadMore}
                disabled={historyLoading}
              >
                {historyLoading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </section>
          </>
          )}
        </div>
      )}
    </div>
  );
}

function KpiTile({
  label, value, hint, muted = false, warn = false, sentence = false, valueClass,
}) {
  return (
    <div className={`${styles.kpi} ${muted ? styles.kpiMuted : ''} ${warn ? styles.kpiWarn : ''}`}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={`${styles.kpiValue} ${sentence ? styles.kpiValueSentence : ''} ${valueClass || ''}`}>
        {value}
      </span>
      {hint && <span className={styles.kpiHint}>{hint}</span>}
    </div>
  );
}

function ChannelRow({ label, qty, max, soldBy, color }) {
  const n = asNumber(qty) || 0;
  return (
    <li className={styles.channelRow}>
      <span className={styles.channelLabel}>{label}</span>
      <div className={styles.channelTrack}>
        <div
          className={styles.channelFill}
          style={{ width: `${max > 0 ? (n / max) * 100 : 0}%`, background: color }}
        />
      </div>
      <span className={styles.channelQty}>{formatInsightQty(qty, soldBy)}</span>
    </li>
  );
}

function EventBlock({ label, event, soldBy, positive = false }) {
  const qty = asNumber(event?.qty);
  let qtyText = '—';
  if (qty != null) {
    if (positive && qty >= 0) qtyText = `+${formatInsightQty(qty, soldBy)}`;
    else if (!positive && qty > 0) qtyText = `-${formatInsightQty(qty, soldBy)}`;
    else qtyText = formatSignedQty(qty, soldBy);
  }
  return (
    <div className={styles.lastEvent}>
      <span className={styles.lastEventLabel}>{label}</span>
      {event ? (
        <>
          <span className={styles.lastEventValue}>
            {formatInsightAt(event.at)}
            {' · '}
            <span className={positive ? styles.pos : styles.neg}>{qtyText}</span>
          </span>
          <span className={styles.lastEventDetail}>
            {[reasonLabel(event.reason_code), event.lot_id].filter(Boolean).join(' · ')}
          </span>
        </>
      ) : (
        <span className={styles.lastEventDetail}>None in this period</span>
      )}
    </div>
  );
}
